import { useContext, useEffect, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import type { UploadedDocument } from '@urbanx/agx.agencies.client';
import { AuthContext } from '@urbanx/agx.auth.client';
import { AgxPhotoUpload } from '@urbanx/agx.ui-components2';
import { getErrorMessage } from 'helpers/getErrorMessage';
import { UploadPublicDocument } from 'services';

interface PhotoUploadProps {
  photoUri?: string;
  agencyId: string;
  disabled?: boolean;
  removePending?: boolean;
  onUploaded: (document: UploadedDocument) => void;
  onRemove: () => void;
  /** Lets the owner hold back other edits to the same photo until the upload settles. */
  onUploadPendingChange?: (pending: boolean) => void;
}

const PhotoUpload = ({
  photoUri,
  agencyId,
  disabled = false,
  removePending = false,
  onUploaded,
  onRemove,
  onUploadPendingChange,
}: PhotoUploadProps) => {
  const { getAuthToken } = useContext(AuthContext);
  const [previewUri, setPreviewUri] = useState<string>();
  const previewUrl = useRef<string | undefined>(undefined);
  const [uploadError, setUploadError] = useState<string>();
  const releasePreview = () => {
    if (previewUrl.current) URL.revokeObjectURL(previewUrl.current);
    previewUrl.current = undefined;
  };
  useEffect(
    () => () => {
      if (previewUrl.current) URL.revokeObjectURL(previewUrl.current);
    },
    []
  );
  const { mutate: uploadPublicDocument, isPending: uploadPending } = useMutation({
    mutationFn: UploadPublicDocument,
    onSuccess: (document) => {
      if (document) onUploaded(document);
    },
    onError: (error) =>
      setUploadError(getErrorMessage(error, 'Unable to upload this file. Please try again.')),
    onSettled: () => {
      // Once settled the avatar shows the saved photo again, so the local preview can go.
      releasePreview();
      setPreviewUri(undefined);
      onUploadPendingChange?.(false);
    },
  });
  const actionsDisabled = disabled || uploadPending || removePending;
  const shownUri = uploadPending ? previewUri : photoUri;

  const upload = (file: File) => {
    if (actionsDisabled) return;
    setUploadError(undefined);
    releasePreview();
    previewUrl.current = URL.createObjectURL(file);
    setPreviewUri(previewUrl.current);
    onUploadPendingChange?.(true);
    uploadPublicDocument({ selectedFile: file, AgencyId: agencyId, getAuthToken });
  };

  return (
    <AgxPhotoUpload
      id="fileUpload"
      photoUri={shownUri}
      disabled={actionsDisabled}
      error={uploadError}
      onFileSelected={upload}
      onRemove={onRemove}
    />
  );
};

export default PhotoUpload;
